/**
 * Paste importer: free-form pasted text -> a canonical {@link Rota}.
 *
 * Managers never hand us clean data. They select a block of a spreadsheet and
 * press Ctrl+C, or copy a table out of a PDF, or save a CSV from a payroll
 * system. That paste arrives with a header row or without one; separated by
 * tabs, commas, semicolons or column-aligned spaces; with quoted fields; with a
 * leading blank column where the spreadsheet had a gutter; and - in the rota
 * this app was built around - with a trailing "Total hours (Incl. breaks)"
 * column that must never be mistaken for Sunday.
 *
 * So this module never throws and never refuses. It makes the best structural
 * guess it can, records *every* decision as a {@link ParseIssue}, and returns a
 * confidence score the review table uses to tell the manager how hard to look
 * before applying the import. Shifts it understands are rewritten to the single
 * canonical `HH:MM-HH:MM` spelling so the scheduler only ever sees one form;
 * anything it does not understand is left exactly as typed, because a human
 * fixing a visible mistake beats a parser silently inventing an answer.
 *
 * Pure logic: no `document`, `window` or `localStorage` in here.
 *
 * @typedef {{ row: number|null, column: string|null, value: string,
 *             code: string, message: string,
 *             severity: 'error'|'warning'|'info' }} ParseIssue
 * @typedef {{ rota: object, issues: ParseIssue[], confidence: number,
 *             detectedColumns: string[] }} ParseResult
 */

import {
  DAY_KEYS,
  DAY_LABELS,
  classifyShiftCell,
  dateForDayKey,
  formatMinutes,
  parseIsoDate,
} from '../core/time.js';
import { ROTA_SCHEMA_VERSION, normaliseRota } from '../core/model.js';

/* -------------------------------------------------------------------------- */
/* Tuning constants                                                           */
/* -------------------------------------------------------------------------- */

/** Single-character delimiters, in preference order. Ties go to the first. */
const CHAR_DELIMITERS = ['\t', ',', ';'];

/** Fallback for text copied out of a PDF or a fixed-width terminal dump. */
const MULTI_SPACE_DELIMITER = /\s{2,}/;

/** Only look at the first slice of a long paste when sniffing the delimiter. */
const DELIMITER_SAMPLE_LINES = 200;

/**
 * How much each severity costs the confidence score. An error means we produced
 * a row a human must repair; a warning means we kept the manager's text because
 * we could not read it; info means we silently rewrote something they typed.
 */
const SEVERITY_WEIGHT = { error: 1, warning: 0.5, info: 0.2 };

/** A first row is a header once more than this share of its cells look like one. */
const HEADER_CONFIDENCE_THRESHOLD = 0.5;

/**
 * How many header cells must resolve to a weekday before we trust the header's
 * own ordering instead of falling back to "columns 1..7 are Mon..Sun". Four is
 * enough to prove the row really is naming days rather than coincidence.
 */
const MIN_MAPPED_DAYS_FOR_HEADER_ORDER = 4;

const MONTH_SHORT = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

/** Header words that identify the person column. */
const NAME_HEADER_RE = /\b(name|names|staff|employee|employees|person|people|colleague|team\s*member)\b/;

/**
 * Header words that identify a summary column. The real rota export ends with
 * "Total hours (Incl.breaks)"; treating that as Sunday would give every member
 * of staff a nonsense seventh shift, so this check is load-bearing.
 */
const DROP_HEADER_RE = /\b(total|totals|hours|hrs|incl\.?\s*breaks?|breaks?\s*incl|weekly|contracted|sum)\b/;

/** Day-of-week tokens, tolerant of the abbreviations spreadsheets produce. */
const DAY_TOKEN_PATTERNS = [
  { dayKey: 'mon', re: /\bmon(day)?\b/ },
  { dayKey: 'tue', re: /\btue(s|sday)?\b/ },
  { dayKey: 'wed', re: /\bwed(s|nesday)?\b/ },
  { dayKey: 'thu', re: /\bthu(r|rs|rsday)?\b/ },
  { dayKey: 'fri', re: /\bfri(day)?\b/ },
  { dayKey: 'sat', re: /\bsat(urday)?\b/ },
  { dayKey: 'sun', re: /\bsun(day)?\b/ },
];

/** "27 Jul", "Jul 27", "27/07/2026", "2026-07-27" - a header cell that is a date. */
const DATE_HEADER_RES = [
  /^\d{1,2}\s*[/-]\s*\d{1,2}(?:\s*[/-]\s*\d{2,4})?$/,
  /^\d{4}-\d{2}-\d{2}$/,
];

/** "40", "47.5", "38 hrs", "47:30" - the shapes a weekly total takes. */
const NUMERIC_TOTAL_RE = /^\d{1,3}(?:[.,]\d{1,2})?\s*(?:h|hr|hrs|hour|hours)?$|^\d{1,3}[:.]\d{2}\s*(?:h|hr|hrs)?$/;

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Parse a block of pasted rota text into a Rota plus a full audit trail.
 *
 * This is the entry point the import dialog uses. It is deliberately the thin
 * composition of {@link parseDelimited} and {@link rowsToRota} so that the PDF
 * importer - which produces a grid of its own - can reuse the identical column
 * mapping and cell normalisation rules and therefore behave identically.
 *
 * @param {string} text Raw clipboard text.
 * @param {{ weekCommencing?: string }} [options] ISO Monday the rota belongs to;
 *   used to resolve date-style headers such as "27 Jul" to a day key.
 * @returns {ParseResult}
 */
export function parsePastedRota(text, { weekCommencing = '' } = {}) {
  const rows = parseDelimited(text);
  return rowsToRota(rows, { weekCommencing, source: 'paste' });
}

/**
 * Split pasted text into a rectangular-ish grid of trimmed cells.
 *
 * Kept separate from {@link rowsToRota} because splitting and interpreting fail
 * in different ways: a manager can see that the split was wrong far more easily
 * than they can see that column 8 was misread as Sunday, so the review table
 * shows the two stages separately.
 *
 * @param {string} text
 * @returns {string[][]} One array per non-empty line. Cells are trimmed and
 *   unquoted; entirely blank lines are dropped.
 */
export function parseDelimited(text) {
  const normalised = normaliseLineEndings(text);
  if (!normalised.trim()) return [];

  const delimiter = detectDelimiter(normalised);
  const rows = delimiter instanceof RegExp
    ? splitByPattern(normalised, delimiter)
    : splitByCharacter(normalised, delimiter);

  return rows
    .map((row) => row.map((cell) => cell.trim()))
    .filter((row) => row.some((cell) => cell !== ''));
}

/**
 * Work out which delimiter the paste actually uses.
 *
 * Counting occurrences is not good enough - a rota full of "08:30-14:30" cells
 * contains no commas at all, while a name like "Lyons, Catherine" contains one.
 * What distinguishes the real delimiter is *consistency*: splitting on it yields
 * the same column count on nearly every line. So each candidate is scored by the
 * share of lines that agree on a modal column count, with the count itself as a
 * tiebreaker (more columns means we found more structure) and tab first in the
 * running order so an exact tie resolves in favour of a spreadsheet paste.
 *
 * @param {string} text
 * @returns {'\t'|','|';'|RegExp} The winning delimiter; tab when nothing scores.
 */
export function detectDelimiter(text) {
  const normalised = normaliseLineEndings(text);
  // Blank lines are dropped but the surviving lines are *not* trimmed: trailing
  // delimiters mark real empty cells (a person who does not work Sunday) and
  // trimming them away would make the grid look ragged and pick the wrong
  // delimiter entirely.
  const lines = normalised
    .split('\n')
    .filter((line) => line.trim() !== '')
    .slice(0, DELIMITER_SAMPLE_LINES);

  if (!lines.length) return '\t';
  const sample = lines.join('\n');

  let best = { delimiter: '\t', consistency: 0, columns: 0 };

  const consider = (delimiter, columnCounts) => {
    const modal = modalValue(columnCounts);
    // A modal count of one means this candidate never split anything.
    if (modal < 2) return;
    const agreeing = columnCounts.filter((count) => count === modal).length;
    const consistency = agreeing / columnCounts.length;
    const better = consistency > best.consistency + 1e-9
      || (Math.abs(consistency - best.consistency) < 1e-9 && modal > best.columns);
    if (better) best = { delimiter, consistency, columns: modal };
  };

  for (const candidate of CHAR_DELIMITERS) {
    consider(candidate, splitByCharacter(sample, candidate).map((row) => row.length));
  }
  consider(MULTI_SPACE_DELIMITER, lines.map((line) => line.trim().split(MULTI_SPACE_DELIMITER).length));

  return best.consistency > 0 ? best.delimiter : '\t';
}

/**
 * Interpret an already-split grid as a week of rota rows.
 *
 * Everything structural happens here - header detection, finding the name
 * column, dropping the weekly-total column, mapping the rest to Mon..Sun - and
 * every one of those decisions is recorded as an issue rather than applied
 * silently, because a wrong column mapping is the single most damaging mistake
 * an importer can make and the manager needs to be able to spot it.
 *
 * Input is never mutated; a brand-new Rota is returned.
 *
 * @param {string[][]} rows Grid of raw cell strings.
 * @param {{ weekCommencing?: string, source?: string }} [options]
 * @returns {ParseResult} `issues[].row` indexes `rota.rows`; structural issues
 *   that belong to no single row carry `row: null`.
 */
export function rowsToRota(rows, { weekCommencing = '', source = 'paste' } = {}) {
  const issues = [];
  const grid = trimGrid(rows);

  if (!grid.length) {
    issues.push(structuralIssue('empty-paste', 'error', 'Nothing to import - the pasted text contained no rows.'));
    return { rota: emptyRota(weekCommencing, source), issues, confidence: 0, detectedColumns: [] };
  }

  const hasHeader = looksLikeHeader(grid[0], weekCommencing);
  const header = hasHeader ? grid[0] : [];
  const dataRows = hasHeader ? grid.slice(1) : grid;

  const layout = resolveLayout({ header, hasHeader, dataRows, weekCommencing, issues });

  if (!layout.dayColumns.length) {
    issues.push(structuralIssue(
      'no-day-columns',
      'error',
      'Could not find any day columns. Expected a name column followed by Monday to Sunday.',
    ));
    return { rota: emptyRota(weekCommencing, source), issues, confidence: 0, detectedColumns: [] };
  }

  const builtRows = [];
  for (const rawRow of dataRows) {
    const name = layout.nameColumn >= 0 ? (rawRow[layout.nameColumn] ?? '').trim() : '';
    const cells = {};
    let hasCellContent = false;
    for (const { index, dayKey } of layout.dayColumns) {
      const value = (rawRow[index] ?? '').trim();
      cells[dayKey] = value;
      if (value) hasCellContent = true;
    }

    // Blank spacer rows are an artefact of the spreadsheet, not the manager's
    // intent, so they disappear without comment.
    if (!name && !hasCellContent) continue;

    const rowIndex = builtRows.length;
    if (!name) {
      issues.push({
        row: rowIndex,
        column: 'name',
        value: '',
        code: 'missing-name',
        message: 'This row has shifts but no name. Add the person before importing, or delete the row.',
        severity: 'error',
      });
    }

    for (const { dayKey } of layout.dayColumns) {
      cells[dayKey] = normaliseCell(cells[dayKey], { rowIndex, dayKey, issues });
    }

    builtRows.push({ employeeId: '', name, cells });
  }

  const rota = normaliseRota({
    schemaVersion: ROTA_SCHEMA_VERSION,
    weekCommencing,
    source,
    importedAt: new Date().toISOString(),
    notes: '',
    rows: builtRows,
  });

  const cellCount = rota.rows.length * (layout.dayColumns.length + (layout.nameColumn >= 0 ? 1 : 0));

  return {
    rota,
    issues,
    confidence: scoreConfidence(issues, cellCount),
    detectedColumns: layout.detectedColumns,
  };
}

/* -------------------------------------------------------------------------- */
/* Splitting                                                                  */
/* -------------------------------------------------------------------------- */

/** Collapse CRLF/CR to LF so every downstream scan only handles one newline. */
function normaliseLineEndings(text) {
  return String(text ?? '').replace(/\r\n?/g, '\n');
}

/**
 * RFC-4180-style scan for single-character delimiters.
 *
 * Written as a character scanner rather than `line.split(delimiter)` because a
 * quoted field may legally contain both the delimiter and a newline - a CSV
 * exported from a payroll system with a note in it will do exactly that, and a
 * naive line split silently shreds the row.
 */
function splitByCharacter(text, delimiter) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"' && field.trim() === '') {
      inQuotes = true;
      field = '';
    } else if (char === delimiter) {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  row.push(field);
  rows.push(row);
  return rows;
}

/**
 * Split column-aligned text (PDF copy/paste) on runs of whitespace.
 * Quotes are still honoured because some sources quote names containing spaces.
 */
function splitByPattern(text, pattern) {
  return text.split('\n').map((line) => line.trim().split(pattern).map(unquote));
}

/** Strip a surrounding pair of double quotes and unescape doubled quotes. */
function unquote(value) {
  const text = String(value ?? '').trim();
  if (text.length >= 2 && text.startsWith('"') && text.endsWith('"')) {
    return text.slice(1, -1).replace(/""/g, '"');
  }
  return text;
}

/** Most frequent value in a list of numbers; ties go to the larger value. */
function modalValue(values) {
  const counts = new Map();
  let best = 0;
  let bestCount = 0;
  for (const value of values) {
    const count = (counts.get(value) ?? 0) + 1;
    counts.set(value, count);
    if (count > bestCount || (count === bestCount && value > best)) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

/* -------------------------------------------------------------------------- */
/* Grid tidying                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Pad the grid to a rectangle and drop leading columns that are empty on every
 * line. Spreadsheet selections routinely start one column to the left of the
 * data; without this the name column would be read as an empty day column and
 * every single row would be reported as missing a name.
 */
function trimGrid(rows) {
  const cleaned = (Array.isArray(rows) ? rows : [])
    .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '').trim()) : []))
    .filter((row) => row.some((cell) => cell !== ''));

  if (!cleaned.length) return [];

  const width = cleaned.reduce((max, row) => Math.max(max, row.length), 0);
  const padded = cleaned.map((row) => {
    const copy = row.slice(0, width);
    while (copy.length < width) copy.push('');
    return copy;
  });

  let leading = 0;
  while (leading < width && padded.every((row) => row[leading] === '')) leading += 1;
  if (leading === 0 || leading >= width) return padded;
  return padded.map((row) => row.slice(leading));
}

/* -------------------------------------------------------------------------- */
/* Header interpretation                                                      */
/* -------------------------------------------------------------------------- */

/** 'blank' | 'name' | 'drop' | 'day' | 'unknown', plus a day key when known. */
function classifyHeaderCell(cell, weekCommencing) {
  const text = String(cell ?? '').trim();
  if (!text) return { kind: 'blank', dayKey: null };

  const lower = text.toLowerCase();

  const token = DAY_TOKEN_PATTERNS.find(({ re }) => re.test(lower));
  if (token) return { kind: 'day', dayKey: token.dayKey };

  if (NAME_HEADER_RE.test(lower)) return { kind: 'name', dayKey: null };
  if (DROP_HEADER_RE.test(lower)) return { kind: 'drop', dayKey: null };

  const dated = dayKeyFromWeekDate(lower, weekCommencing);
  if (dated) return { kind: 'day', dayKey: dated };

  // A date we recognise but cannot pin to a weekday (no week-commencing was
  // supplied) still proves this row is a header. It is reported as a day column
  // with no key, which forces the positional Mon..Sun fallback below.
  if (looksLikeDateHeader(lower)) return { kind: 'day', dayKey: null };

  return { kind: 'unknown', dayKey: null };
}

/** True when a header cell reads as a calendar date of any common shape. */
function looksLikeDateHeader(lowerCell) {
  if (classifyShiftCell(lowerCell).kind === 'shift') return false;
  if (DATE_HEADER_RES.some((re) => re.test(lowerCell))) return true;
  const hasMonth = MONTH_SHORT.some((month) => lowerCell.includes(month));
  return hasMonth && /\b\d{1,2}(?:st|nd|rd|th)?\b/.test(lowerCell);
}

/**
 * Resolve a bare date header such as "27 Jul", "27/07/2026" or "27th" to a day
 * key by testing it against the seven real dates of the week being imported.
 *
 * Matching against the known week rather than parsing the date in the abstract
 * removes the whole problem of missing years, US vs UK ordering and two-digit
 * years: the seven days of a week always have distinct dates of the month, so
 * one match is unambiguous.
 */
function dayKeyFromWeekDate(lowerCell, weekCommencing) {
  if (!weekCommencing) return null;
  // A cell that reads as a shift is data, not a header - "08:30-14:30" contains
  // a "30" that would otherwise look like a date of the month.
  if (classifyShiftCell(lowerCell).kind === 'shift') return null;

  // Remove clock times before hunting for a day of the month.
  const cleaned = lowerCell.replace(/\d{1,2}\s*[:.]\s*\d{2}/g, ' ');
  const numbers = [...cleaned.matchAll(/\b(\d{1,2})(?:st|nd|rd|th)?\b/g)].map((match) => Number(match[1]));
  if (!numbers.length) return null;

  const mentionedMonth = MONTH_SHORT.findIndex((month) => cleaned.includes(month));
  const numericPair = cleaned.match(/\b(\d{1,2})\s*[/-]\s*(\d{1,2})\b/);

  for (const dayKey of DAY_KEYS) {
    const date = parseIsoDate(dateForDayKey(weekCommencing, dayKey));
    if (!date) continue;

    const dayOfMonth = date.getUTCDate();
    const monthIndex = date.getUTCMonth();
    if (!numbers.includes(dayOfMonth)) continue;
    if (mentionedMonth >= 0 && mentionedMonth !== monthIndex) continue;

    if (numericPair) {
      const first = Number(numericPair[1]);
      const second = Number(numericPair[2]);
      const ukOrder = first === dayOfMonth && second === monthIndex + 1;
      const usOrder = second === dayOfMonth && first === monthIndex + 1;
      if (!ukOrder && !usOrder) continue;
    }

    return dayKey;
  }

  return null;
}

/**
 * Decide whether the first row names the columns or is already a person.
 *
 * Recognisable headers vote yes; anything that parses as a real shift votes no,
 * and votes hard, because a shift in row one is conclusive proof there is no
 * header and mistakenly eating that row loses a member of staff entirely.
 */
function looksLikeHeader(row, weekCommencing) {
  const cells = (row ?? []).filter((cell) => cell !== '');
  if (!cells.length) return false;

  let votes = 0;
  for (const cell of cells) {
    if (classifyShiftCell(cell).kind === 'shift') {
      votes -= 1;
      continue;
    }
    const { kind } = classifyHeaderCell(cell, weekCommencing);
    if (kind === 'day' || kind === 'name' || kind === 'drop') votes += 1;
  }

  return votes / cells.length > HEADER_CONFIDENCE_THRESHOLD;
}

/* -------------------------------------------------------------------------- */
/* Column layout                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Work out which source column is the name, which are days, and which are
 * summary columns to throw away.
 *
 * Two strategies, in order of trustworthiness: believe an explicit header when
 * it resolves the days unambiguously, otherwise fall back on the near-universal
 * "name, then Monday to Sunday" shape.
 */
function resolveLayout({ header, hasHeader, dataRows, weekCommencing, issues }) {
  const width = (hasHeader ? header : dataRows[0] ?? []).length;
  const classified = hasHeader
    ? header.map((cell) => classifyHeaderCell(cell, weekCommencing))
    : [];

  const dropped = new Set();
  if (hasHeader) {
    classified.forEach((info, index) => {
      if (info.kind !== 'drop') return;
      dropped.add(index);
      issues.push(structuralIssue(
        'dropped-column',
        'info',
        `Ignored the "${header[index]}" column - it is a weekly summary, not a day.`,
        { column: `col-${index}`, value: header[index] },
      ));
    });
  }

  const nameColumn = findNameColumn({ hasHeader, classified, dropped, width });

  const available = [];
  for (let index = 0; index < width; index += 1) {
    if (index === nameColumn || dropped.has(index)) continue;
    available.push(index);
  }

  const mappedByHeader = [];
  let unresolvedDayHeaders = 0;
  if (hasHeader) {
    const used = new Set();
    for (const index of available) {
      const { kind, dayKey } = classified[index] ?? {};
      if (!dayKey) {
        // A date header we could not pin to a weekday. Counted so that a
        // partially-resolved header never leaves those columns unmapped.
        if (kind === 'day') unresolvedDayHeaders += 1;
        continue;
      }
      if (used.has(dayKey)) {
        issues.push(structuralIssue(
          'duplicate-day-column',
          'warning',
          `Ignored a second "${DAY_LABELS[dayKey]}" column ("${header[index]}"); the first one was used.`,
          { column: `col-${index}`, value: header[index] },
        ));
        continue;
      }
      used.add(dayKey);
      mappedByHeader.push({ index, dayKey });
    }
  }

  // Believe the header's own ordering when it names enough days to prove it is
  // really a header, or when it accounts for every remaining column (a short
  // three-day extract with the days out of order is still unambiguous). Any
  // date header we failed to resolve sends us to the positional fallback,
  // because half a mapping is worse than none.
  const trustHeaderOrder = unresolvedDayHeaders === 0
    && (mappedByHeader.length >= MIN_MAPPED_DAYS_FOR_HEADER_ORDER
      || (mappedByHeader.length > 0 && mappedByHeader.length === available.length));

  let dayColumns;
  if (trustHeaderOrder) {
    dayColumns = mappedByHeader;
    const claimed = new Set(mappedByHeader.map((entry) => entry.index));
    for (const index of available) {
      if (claimed.has(index)) continue;
      issues.push(structuralIssue(
        'dropped-column',
        'info',
        `Ignored the "${header[index] || `column ${index + 1}`}" column - it does not name a day of the week.`,
        { column: `col-${index}`, value: header[index] ?? '' },
      ));
    }
  } else {
    dayColumns = mapPositionally({ available, dataRows, header, hasHeader, issues });
  }

  // Sort into calendar order so the review table always reads Mon -> Sun even
  // when the source listed the days in some other order.
  dayColumns = dayColumns
    .slice()
    .sort((a, b) => DAY_KEYS.indexOf(a.dayKey) - DAY_KEYS.indexOf(b.dayKey));

  const detectedColumns = [
    ...(nameColumn >= 0 ? ['Name'] : []),
    ...dayColumns.map((entry) => DAY_LABELS[entry.dayKey]),
  ];

  return { nameColumn, dayColumns, detectedColumns };
}

/**
 * The name column is whichever column the header says it is; failing that, the
 * first column that is not already spoken for. Only when the header maps every
 * column to a day do we accept that there is no name column at all.
 */
function findNameColumn({ hasHeader, classified, dropped, width }) {
  if (hasHeader) {
    const explicit = classified.findIndex((info) => info.kind === 'name');
    if (explicit >= 0) return explicit;
    for (let index = 0; index < width; index += 1) {
      if (dropped.has(index)) continue;
      if (classified[index]?.kind === 'day') break;
      return index;
    }
    return -1;
  }
  return width > 0 ? 0 : -1;
}

/**
 * Assign the remaining columns to Monday..Sunday left to right.
 *
 * Before doing so, trailing columns that hold weekly totals rather than shifts
 * are stripped. Without a header there is no wording to go on, so the give-away
 * is the content itself: a column of bare numbers with not one parseable shift
 * in it is a total, not a day.
 */
function mapPositionally({ available, dataRows, header, hasHeader, issues }) {
  const columns = available.slice();
  const describe = (index) => (hasHeader && header[index] ? `"${header[index]}"` : `column ${index + 1}`);

  while (columns.length > 0) {
    const last = columns[columns.length - 1];
    const overflowing = columns.length > DAY_KEYS.length;
    const values = dataRows.map((row) => (row[last] ?? '').trim()).filter(Boolean);

    if (overflowing && values.length === 0) {
      columns.pop();
      continue;
    }
    if (looksLikeTotalsColumn(values)) {
      columns.pop();
      issues.push(structuralIssue(
        'dropped-column',
        'info',
        `Ignored ${describe(last)} - it holds weekly totals rather than shifts.`,
        { column: `col-${last}`, value: hasHeader ? header[last] ?? '' : '' },
      ));
      continue;
    }
    if (overflowing) {
      columns.pop();
      issues.push(structuralIssue(
        'extra-column',
        'warning',
        `Ignored ${describe(last)} - there are already seven day columns before it.`,
        { column: `col-${last}`, value: hasHeader ? header[last] ?? '' : '' },
      ));
      continue;
    }
    break;
  }

  return columns
    .slice(0, DAY_KEYS.length)
    .map((index, position) => ({ index, dayKey: DAY_KEYS[position] }));
}

/** True when every populated value is a bare hours figure and none is a shift. */
function looksLikeTotalsColumn(values) {
  if (values.length < 2) return false;
  return values.every((value) => {
    if (classifyShiftCell(value).kind !== 'unparsed') return false;
    return NUMERIC_TOTAL_RE.test(value);
  });
}

/* -------------------------------------------------------------------------- */
/* Cell normalisation                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Rewrite one cell to canonical form, recording what changed.
 *
 * Recognised shifts become `HH:MM-HH:MM` so that "8:30 - 2:30pm", "08.30-14.30"
 * and "08:30–14:30" all reach the scheduler as one string. Absence markers keep
 * the manager's own vocabulary - `classifyShiftCell` already understands them
 * and rewriting them would only make the review table look like it had
 * misunderstood something. Anything unreadable is returned untouched together
 * with a warning, because the manager can fix a visible "9-5ish" in the editor
 * but cannot fix a guess the parser made on their behalf.
 */
function normaliseCell(value, { rowIndex, dayKey, issues }) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const classified = classifyShiftCell(raw);

  if (classified.kind === 'shift') {
    const { startMinutes, endMinutes } = classified.shift;
    const canonical = `${formatMinutes(startMinutes)}-${formatMinutes(endMinutes)}`;
    if (canonical !== raw) {
      issues.push({
        row: rowIndex,
        column: dayKey,
        value: raw,
        code: 'normalised-shift',
        message: `${DAY_LABELS[dayKey]}: read "${raw}" as ${canonical}.`,
        severity: 'info',
      });
    }
    return canonical;
  }

  if (classified.kind === 'unparsed') {
    issues.push({
      row: rowIndex,
      column: dayKey,
      value: raw,
      code: 'unparsed-shift',
      message: `${DAY_LABELS[dayKey]}: could not read "${raw}" as a shift. It has been left as typed - edit it below.`,
      severity: 'warning',
    });
  }

  return raw;
}

/* -------------------------------------------------------------------------- */
/* Scoring & shells                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Turn the issue list into a single 0..1 number for the import dialog.
 *
 * Only issues that point at a real cell count. A structural decision such as
 * dropping the "Total hours (Incl.breaks)" column is something we understood
 * completely, so penalising it would tell the manager to distrust a perfectly
 * good import. Severities are weighted because one unreadable cell in two
 * hundred is noise, whereas a row with no name at all is a blocker.
 */
function scoreConfidence(issues, cellCount) {
  if (cellCount <= 0) return 0;
  const weighted = issues
    .filter((issue) => issue.row !== null)
    .reduce((total, issue) => total + (SEVERITY_WEIGHT[issue.severity] ?? 0.5), 0);
  const confidence = 1 - weighted / cellCount;
  return Math.round(Math.min(1, Math.max(0, confidence)) * 1000) / 1000;
}

/** A ParseIssue about the shape of the paste rather than about one cell. */
function structuralIssue(code, severity, message, { column = null, value = '' } = {}) {
  return { row: null, column, value, code, message, severity };
}

/** An empty but structurally valid Rota, so callers never get null back. */
function emptyRota(weekCommencing, source) {
  return normaliseRota({
    schemaVersion: ROTA_SCHEMA_VERSION,
    weekCommencing,
    source,
    importedAt: new Date().toISOString(),
    notes: '',
    rows: [],
  });
}
